import { useState, useCallback, useMemo, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { 
  generateResponsiveImageSources, 
  getOptimizedImageUrl,
  generateSizesAttribute,
  generateBlurPlaceholder
} from '@/utils/imageOptimization'

interface OptimizedImageProps {
  src: string | null
  alt: string
  className?: string
  fallbackText?: string
  preferredSize?: 'small' | 'medium' | 'large'
  onLoad?: (src: string) => void
  priority?: boolean
  responsive?: boolean
  sizes?: string
  layout?: 'hero' | 'card' | 'thumbnail' | 'full-width' | 'custom'
  width?: number
  height?: number
  blurPlaceholder?: boolean
  loading?: 'eager' | 'lazy'
  style?: React.CSSProperties
}

export default function OptimizedImage({
  src,
  alt,
  className = '',
  fallbackText = 'Ingen bild',
  onLoad,
  priority = false,
  responsive = true,
  sizes,
  layout = 'card',
  width,
  height,
  blurPlaceholder = false,
  loading = 'lazy',
  style,
}: OptimizedImageProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [currentSrc, setCurrentSrc] = useState<string | null>(null)
  const [transformFailed, setTransformFailed] = useState(false)

  // Enable optimization with Supabase Transform API
  const { optimizedSrc, originalSrc } = useMemo(() => {
    if (!src) {
      return { optimizedSrc: null, originalSrc: null };
    }
    
    let baseSrc: string;
    if (typeof src === 'string') {
      baseSrc = src;
    } else {
      baseSrc = (src as any)?.data?.attributes?.url || (src as any)?.url;
    }
    
    if (!baseSrc) {
      return { optimizedSrc: null, originalSrc: null };
    }

    // Use Supabase Transform API for optimization
    const targetWidth = width ? Number(width) : undefined;
    const optimized = getOptimizedImageUrl(baseSrc, targetWidth, layout);
    
    return { 
      optimizedSrc: optimized,
      originalSrc: baseSrc 
    };
  }, [src, layout, width])

  // Reset transformFailed when src changes
  useEffect(() => {
    setTransformFailed(false);
  }, [src])

  // Set source and preload critical images
  useEffect(() => {
    if (optimizedSrc) {
      setCurrentSrc(optimizedSrc);
      
      // Preload critical images for faster TTI
      if (priority && layout === 'hero') {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = optimizedSrc;
        document.head.appendChild(link);
        
        // Clean up preload link
        setTimeout(() => link.parentNode?.removeChild(link), 10000);
      }
    } else {
      setCurrentSrc(null);
    }
  }, [optimizedSrc, priority, layout])

  const handleLoad = useCallback(() => {
    setIsLoading(false)
    if (onLoad && currentSrc) {
      onLoad(currentSrc)
    }
  }, [onLoad, currentSrc])

  const handleError = useCallback(() => {
    // If transform failed, try loading the original image
    if (!transformFailed && originalSrc && currentSrc !== originalSrc) {
      console.log('[OptimizedImage] Transform failed, falling back to original:', originalSrc);
      setTransformFailed(true);
      setCurrentSrc(originalSrc);
    } else {
      // Both transform and original failed, or no original available
      console.warn('[OptimizedImage] Image failed to load:', currentSrc);
      setIsLoading(false);
    }
  }, [transformFailed, originalSrc, currentSrc])


  // Fast fallback rendering
  if (!optimizedSrc) {
    return (
      <div className={cn("bg-muted flex items-center justify-center", className)}>
        <span className="text-muted-foreground text-sm">{fallbackText}</span>
      </div>
    )
  }

  if (!currentSrc) {
    return <div className={cn("bg-muted animate-pulse", className)} />
  }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      <img
        src={currentSrc}
        alt={alt}
        className={className}
        style={style}
        loading={priority ? "eager" : loading}
        decoding="async"
        {...(priority ? { fetchpriority: "high" } : {})}
        onLoad={handleLoad}
        onError={handleError}
        width={width}
        height={height}
      />
    </div>
  )
}